import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Box, Stack, Text } from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import { PromoBoxRendering } from '@sitecore/types/manual/PromoBox';

import {
  circleBoxRecipe,
  promoBoxContainer,
  promoBoxWrapperRecipe,
  titleText,
  titleTextOffset,
  subTitleText,
} from './PromoBox.css';

const COLOR_TEXT_MAP = {
  backgroundCMSVarTwelve: 'textInverted', // Purple, white text
  backgroundCMSVarTen: 'textInverted', // Dark green, white text
  backgroundCMSVarEleven: 'textPrimary', // Light gray, black text
  backgroundCMSVarNine: 'textBrand', // Light green, dark green text
} as const;

type TextColor = (typeof COLOR_TEXT_MAP)[keyof typeof COLOR_TEXT_MAP];

const getTextColor = (backgroundColor?: string): TextColor => {
  if (!backgroundColor) return 'textBrand';
  return COLOR_TEXT_MAP[backgroundColor as keyof typeof COLOR_TEXT_MAP] || 'textPrimary';
};

const PromoBox: FC<PromoBoxRendering> = ({ fields, params }) => {
  const { content } = fields;
  const isElevated = params?.setElevationFlag === '1';
  const backgroundColor = params?.backgroundColorOption || 'backgroundCMSVarNine';
  const desktopPosition = params?.desktopVariantOption;
  const mobilePosition = params?.mobileVariantOption;
  const textColor = getTextColor(params?.backgroundColorOption);

  const hasBothSubtitles = Boolean(content?.topSubTitle?.value && content?.bottomSubTitle?.value);

  return (
    <Box className={promoBoxContainer}>
      <Box className={promoBoxWrapperRecipe({ desktopPosition, mobilePosition })}>
        <Box
          backgroundColor={backgroundColor}
          borderRadius="round"
          className={circleBoxRecipe({ elevated: isElevated })}>
          <Stack gap="1" alignX="center">
            {content?.topSubTitle?.value && (
              <Stack.Item>
                <Text color={textColor} className={subTitleText}>
                  {wrap(content.topSubTitle)}
                </Text>
              </Stack.Item>
            )}
            {content?.title?.value && (
              <Stack.Item>
                <Text color={textColor} className={hasBothSubtitles ? `${titleText} ${titleTextOffset}` : titleText}>
                  {wrap(content.title, <RichText html={content.title.value} />)}
                </Text>
              </Stack.Item>
            )}
            {content?.bottomSubTitle?.value && (
              <Stack.Item>
                <Text color={textColor} className={subTitleText}>
                  {wrap(content.bottomSubTitle)}
                </Text>
              </Stack.Item>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export { PromoBox };
export default PromoBox;
